import {
  Check,
  CheckCircle2,
  Clipboard,
  ClipboardCheck,
  CircleX,
  Clock3,
  GitCommitHorizontal,
  KeyRound,
  LoaderCircle,
  Plus,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  UserRoundCog,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  approveRequest,
  createMember,
  getApprovals,
  getMembers,
  getPolicy,
  rejectRequest,
  updateMember,
  updatePolicy,
} from "../api";
import type {
  ApprovalRequest,
  AuthSession,
  IncidentItem,
  PolicyVersion,
  QodeItem,
  TimelineEvent,
  WorkspacePolicy,
  WorkspaceMember,
  WorkspaceRole,
} from "../types";
import { formatRelativeTime } from "../lib/format";

type GovernanceViewProps = {
  qodes: QodeItem[];
  session: AuthSession;
  onIncidentChanged: (incident: IncidentItem) => void;
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
};

const roleLabels: Record<WorkspaceRole, string> = {
  owner: "Owner",
  admin: "Admin",
  approver: "Approver",
  operator: "Operator",
  analyst: "Analyst",
  viewer: "Viewer",
};

const allRoles: WorkspaceRole[] = [
  "owner",
  "admin",
  "approver",
  "operator",
  "analyst",
  "viewer",
];

const approvalStatusLabels: Record<ApprovalRequest["status"], string> = {
  pending: "待审批",
  executing: "执行中",
  approved: "已通过",
  rejected: "已拒绝",
  expired: "已过期",
};

export function GovernanceView({
  qodes,
  session,
  onIncidentChanged,
  onQodeChanged,
}: GovernanceViewProps) {
  const [activeTab, setActiveTab] = useState<
    "members" | "approvals" | "policy"
  >(
    session.capabilities.manageMembers ? "members" : "approvals",
  );
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(session.capabilities.manageMembers);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(true);
  const [policy, setPolicy] = useState<WorkspacePolicy | null>(null);
  const [policyVersions, setPolicyVersions] = useState<PolicyVersion[]>([]);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyEnabled, setPolicyEnabled] = useState(false);
  const [policyThreshold, setPolicyThreshold] = useState("100");
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("operator");
  const [working, setWorking] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [approvalWorking, setApprovalWorking] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReasons, setRejectionReasons] = useState<
    Record<string, string>
  >({});

  const assignableRoles = useMemo(
    () =>
      session.role === "owner"
        ? allRoles
        : allRoles.filter((item) => item !== "owner" && item !== "admin"),
    [session.role],
  );

  useEffect(() => {
    let active = true;
    setMembers([]);
    setApprovals([]);
    setTemporaryPassword("");
    setCopied(false);
    setCreateOpen(false);
    setRejectingId(null);
    setRejectionReasons({});
    setError("");
    setActiveTab(session.capabilities.manageMembers ? "members" : "approvals");
    setLoading(session.capabilities.manageMembers);
    setApprovalsLoading(true);
    setPolicyLoading(true);
    setPolicy(null);
    setPolicyVersions([]);

    const loads: Promise<void>[] = [
      getApprovals()
        .then((response) => {
          if (active) setApprovals(response.approvals);
        })
        .catch((loadError) => {
          if (active) {
            setError(
              loadError instanceof Error
                ? loadError.message
                : "无法读取审批列表。",
            );
          }
        })
        .finally(() => {
          if (active) setApprovalsLoading(false);
        }),
      getPolicy()
        .then((response) => {
          if (!active) return;
          setPolicy(response.policy);
          setPolicyVersions(response.versions);
          setPolicyEnabled(response.policy.requireIndependentApproval);
          setPolicyThreshold(String(response.policy.highImpactThreshold));
        })
        .catch((loadError) => {
          if (active) {
            setError(
              loadError instanceof Error
                ? loadError.message
                : "无法读取 Workspace 策略。",
            );
          }
        })
        .finally(() => {
          if (active) setPolicyLoading(false);
        }),
    ];
    if (session.capabilities.manageMembers) {
      loads.push(
        getMembers()
          .then((response) => {
            if (active) setMembers(response.members);
          })
          .catch((loadError) => {
            if (active) {
              setError(
                loadError instanceof Error
                  ? loadError.message
                  : "无法读取成员列表。",
              );
            }
          })
          .finally(() => {
            if (active) setLoading(false);
          }),
      );
    }
    void Promise.all(loads);
    return () => {
      active = false;
    };
  }, [session.capabilities.manageMembers, session.workspace.id]);

  const decideApproval = async (
    approval: ApprovalRequest,
    decision: "approve" | "reject",
  ) => {
    setApprovalWorking(approval.id);
    setError("");
    try {
      const response =
        decision === "approve"
          ? await approveRequest(approval.id)
          : await rejectRequest(
              approval.id,
              rejectionReasons[approval.id]?.trim() ?? "",
            );
      setApprovals((current) =>
        current.map((item) =>
          item.id === response.approval.id ? response.approval : item,
        ),
      );
      if (response.result) {
        onQodeChanged(response.result.qode, response.result.event);
      }
      if (response.incident) {
        onIncidentChanged(response.incident);
      }
      setRejectingId(null);
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "审批处理失败。",
      );
    } finally {
      setApprovalWorking(null);
    }
  };

  const addMember = async () => {
    setWorking("create");
    setError("");
    try {
      const response = await createMember({
        username: username.trim(),
        displayName: displayName.trim(),
        role,
      });
      setMembers((current) => [...current, response.member]);
      setTemporaryPassword(response.temporaryPassword ?? "");
      setUsername("");
      setDisplayName("");
      setRole("operator");
      setCreateOpen(false);
      void getPolicy()
        .then((policyResponse) => {
          setPolicy(policyResponse.policy);
          setPolicyVersions(policyResponse.versions);
          setPolicyEnabled(
            policyResponse.policy.requireIndependentApproval,
          );
          setPolicyThreshold(
            String(policyResponse.policy.highImpactThreshold),
          );
        })
        .catch(() => undefined);
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "成员添加失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const mutateMember = async (
    member: WorkspaceMember,
    changes: Partial<Pick<WorkspaceMember, "role" | "status">>,
  ) => {
    setWorking(member.userId);
    setError("");
    try {
      const response = await updateMember(member.userId, changes);
      setMembers((current) =>
        current.map((item) =>
          item.userId === response.member.userId ? response.member : item,
        ),
      );
      void getPolicy()
        .then((policyResponse) => {
          setPolicy(policyResponse.policy);
          setPolicyVersions(policyResponse.versions);
          setPolicyEnabled(
            policyResponse.policy.requireIndependentApproval,
          );
          setPolicyThreshold(
            String(policyResponse.policy.highImpactThreshold),
          );
        })
        .catch(() => undefined);
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "成员更新失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const copyTemporaryPassword = async () => {
    try {
      await navigator.clipboard.writeText(temporaryPassword);
      setCopied(true);
    } catch {
      setError("浏览器未允许写入剪贴板。");
    }
  };

  const savePolicy = async () => {
    setWorking("policy");
    setError("");
    try {
      const response = await updatePolicy({
        requireIndependentApproval: policyEnabled,
        highImpactThreshold: Number(policyThreshold),
      });
      setPolicy(response.policy);
      setPolicyVersions(response.versions);
      setPolicyEnabled(response.policy.requireIndependentApproval);
      setPolicyThreshold(String(response.policy.highImpactThreshold));
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "策略更新失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const protectedQodeCount = policy
    ? qodes.filter(
        (qode) => qode.deployments >= Number(policyThreshold || 0),
      ).length
    : 0;

  return (
    <div className="page governance-page">
      <section className="page-heading">
        <div>
          <h1>Governance</h1>
          <p>管理 {session.workspace.name} 的成员、审批与公共变更。</p>
        </div>
        {activeTab === "members" && session.capabilities.manageMembers ? (
          <button
            className="primary-button"
            type="button"
            onClick={() => setCreateOpen((current) => !current)}
          >
            <Plus size={16} />
            添加成员
          </button>
        ) : null}
      </section>

      <div className="governance-tabs" role="tablist">
        <button
          className={activeTab === "members" ? "active" : ""}
          type="button"
          role="tab"
          aria-selected={activeTab === "members"}
          onClick={() => setActiveTab("members")}
        >
          <Users size={15} />
          成员
        </button>
        <button
          className={activeTab === "approvals" ? "active" : ""}
          type="button"
          role="tab"
          aria-selected={activeTab === "approvals"}
          onClick={() => setActiveTab("approvals")}
        >
          <ClipboardCheck size={15} />
          审批
          {approvals.filter((item) => item.status === "pending").length ? (
            <span>
              {approvals.filter((item) => item.status === "pending").length}
            </span>
          ) : null}
        </button>
        <button
          className={activeTab === "policy" ? "active" : ""}
          type="button"
          role="tab"
          aria-selected={activeTab === "policy"}
          onClick={() => setActiveTab("policy")}
        >
          <SlidersHorizontal size={15} />
          策略
        </button>
      </div>

      {activeTab === "members" && session.capabilities.manageMembers ? (
        <section className="governance-summary">
          <div>
            <span>成员</span>
            <strong>{members.length}</strong>
          </div>
          <div>
            <span>有效访问</span>
            <strong>
              {members.filter((member) => member.status === "active").length}
            </strong>
          </div>
          <div>
            <span>治理角色</span>
            <strong>
              {
                members.filter((member) =>
                  ["owner", "admin", "approver"].includes(member.role),
                ).length
              }
            </strong>
          </div>
        </section>
      ) : null}

      {activeTab === "members" &&
      createOpen &&
      session.capabilities.manageMembers ? (
        <section className="member-create">
          <div className="drawer-section-title">
            <div>
              <h3>添加 Workspace 成员</h3>
              <span>新账号会生成一次性临时密码。</span>
            </div>
            <UserRoundCog size={18} />
          </div>
          <div className="member-create-fields">
            <label>
              账号
              <input
                value={username}
                placeholder="name"
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>
            <label>
              显示名称
              <input
                value={displayName}
                placeholder="成员姓名"
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>
            <label>
              角色
              <select
                value={role}
                onChange={(event) =>
                  setRole(event.target.value as WorkspaceRole)
                }
              >
                {assignableRoles.map((item) => (
                  <option value={item} key={item}>
                    {roleLabels[item]}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="primary-button"
              type="button"
              disabled={
                working !== null ||
                username.trim().length < 3 ||
                !displayName.trim()
              }
              onClick={addMember}
            >
              {working === "create" ? (
                <LoaderCircle className="spin" size={15} />
              ) : (
                <Plus size={15} />
              )}
              添加
            </button>
          </div>
        </section>
      ) : null}

      {activeTab === "members" && temporaryPassword ? (
        <section className="temporary-password" aria-live="polite">
          <KeyRound size={19} />
          <div>
            <strong>临时密码仅显示一次</strong>
            <code>{temporaryPassword}</code>
          </div>
          <button type="button" onClick={copyTemporaryPassword}>
            {copied ? <Check size={15} /> : <Clipboard size={15} />}
            {copied ? "已复制" : "复制"}
          </button>
        </section>
      ) : null}

      {error ? <div className="action-error">{error}</div> : null}

      {activeTab === "members" && !session.capabilities.manageMembers ? (
        <section className="governance-readonly">
          <ShieldCheck size={24} />
          <div>
            <strong>当前角色不管理成员</strong>
            <span>Owner 或 Admin 可以维护 Workspace 成员与角色。</span>
          </div>
        </section>
      ) : null}

      {activeTab === "members" && session.capabilities.manageMembers ? (
        <section className="member-table">
          <div className="member-table-head">
            <span>成员</span>
            <span>角色</span>
            <span>状态</span>
            <span>身份</span>
          </div>
          {loading ? (
            <div className="drawer-loading">
              <LoaderCircle className="spin" size={18} />
              正在读取成员
            </div>
          ) : null}
          {!loading
            ? members.map((member) => {
                const protectedFromAdmin =
                  session.role === "admin" &&
                  (member.role === "owner" || member.role === "admin");
                return (
                  <article className="member-row" key={member.userId}>
                    <div className="member-identity">
                      <span>{member.displayName.slice(0, 1).toUpperCase()}</span>
                      <div>
                        <strong>{member.displayName}</strong>
                        <small>@{member.username}</small>
                      </div>
                    </div>
                    <select
                      aria-label={`调整 ${member.displayName} 的角色`}
                      value={member.role}
                      disabled={working !== null || protectedFromAdmin}
                      onChange={(event) =>
                        void mutateMember(member, {
                          role: event.target.value as WorkspaceRole,
                        })
                      }
                    >
                      {(session.role === "owner"
                        ? allRoles
                        : protectedFromAdmin
                          ? [member.role]
                          : assignableRoles
                      ).map((item) => (
                        <option value={item} key={item}>
                          {roleLabels[item]}
                        </option>
                      ))}
                    </select>
                    <button
                      className={`member-status member-status-${member.status}`}
                      type="button"
                      disabled={working !== null || protectedFromAdmin}
                      onClick={() =>
                        void mutateMember(member, {
                          status:
                            member.status === "active" ? "disabled" : "active",
                        })
                      }
                    >
                      <i />
                      {member.status === "active" ? "有效" : "已停用"}
                    </button>
                    <span className="member-kind">
                      {working === member.userId ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : member.isSuperAdmin ? (
                        "Super Admin"
                      ) : (
                        "Workspace"
                      )}
                    </span>
                  </article>
                );
              })
            : null}
        </section>
      ) : null}

      {activeTab === "policy" ? (
        <section className="policy-panel">
          {policyLoading ? (
            <div className="drawer-loading">
              <LoaderCircle className="spin" size={18} />
              正在读取策略
            </div>
          ) : policy ? (
            <>
              <div className="policy-metrics">
                <div>
                  <span>当前版本</span>
                  <strong>v{policy.version}</strong>
                </div>
                <div>
                  <span>治理成员</span>
                  <strong>{policy.activeGovernanceMembers}</strong>
                </div>
                <div>
                  <span>高影响阈值</span>
                  <strong>
                    {Number(policyThreshold || 0).toLocaleString("zh-CN")}
                  </strong>
                </div>
                <div>
                  <span>达到阈值</span>
                  <strong>{protectedQodeCount}</strong>
                </div>
              </div>
              <div className="policy-controls">
                <div className="policy-control-copy">
                  <strong>高影响变更独立审批</strong>
                  <span>
                    {policyEnabled
                      ? "达到阈值的发布与回滚必须由另一名治理成员批准"
                      : "当前允许治理角色直接发布与回滚"}
                  </span>
                </div>
                <label className="policy-switch">
                  <input
                    type="checkbox"
                    checked={policyEnabled}
                    disabled={
                      !session.capabilities.manageMembers ||
                      (!policyEnabled &&
                        !policy.canEnableIndependentApproval) ||
                      working !== null
                    }
                    onChange={(event) =>
                      setPolicyEnabled(event.target.checked)
                    }
                  />
                  <span aria-hidden="true" />
                  <b>{policyEnabled ? "启用" : "关闭"}</b>
                </label>
                <label className="policy-threshold">
                  高影响数量
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={policyThreshold}
                    disabled={
                      !session.capabilities.manageMembers || working !== null
                    }
                    onChange={(event) =>
                      setPolicyThreshold(event.target.value)
                    }
                  />
                </label>
                {session.capabilities.manageMembers ? (
                  <button
                    className="primary-button"
                    type="button"
                    disabled={
                      working !== null ||
                      !Number.isInteger(Number(policyThreshold)) ||
                      Number(policyThreshold) < 1 ||
                      (policyEnabled &&
                        !policy.canEnableIndependentApproval)
                    }
                    onClick={() => void savePolicy()}
                  >
                    {working === "policy" ? (
                      <LoaderCircle className="spin" size={15} />
                    ) : (
                      <Save size={15} />
                    )}
                    保存
                  </button>
                ) : (
                  <span className="policy-readonly">只读</span>
                )}
              </div>
              {!policy.canEnableIndependentApproval && !policyEnabled ? (
                <div className="policy-guard">
                  <ShieldCheck size={17} />
                  至少需要两名有效的 Owner、Admin 或 Approver
                </div>
              ) : null}
              {policyVersions.length ? (
                <div className="policy-version-list">
                  <div className="section-title">
                    <div>
                      <h2>策略版本</h2>
                      <span>每次有效变更都保留不可修改快照</span>
                    </div>
                  </div>
                  {policyVersions.slice(0, 6).map((version) => (
                    <div key={version.id}>
                      <GitCommitHorizontal size={15} />
                      <strong>v{version.version}</strong>
                      <span>
                        {version.requireIndependentApproval
                          ? "独立审批"
                          : "直接治理"}{" "}
                        · 阈值{" "}
                        {version.highImpactThreshold.toLocaleString("zh-CN")}
                      </span>
                      <small>
                        {version.changedBy?.displayName ?? "系统迁移"} ·{" "}
                        {formatRelativeTime(version.changedAt)}
                      </small>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {activeTab === "approvals" ? (
        <section className="approval-table">
          <div className="approval-table-head">
            <span>Qode / 动作</span>
            <span>请求人</span>
            <span>请求内容</span>
            <span>状态</span>
            <span>处理</span>
          </div>
          {approvalsLoading ? (
            <div className="drawer-loading">
              <LoaderCircle className="spin" size={18} />
              正在读取审批
            </div>
          ) : null}
          {!approvalsLoading && !approvals.length ? (
            <div className="approval-empty">
              <ClipboardCheck size={20} />
              暂无审批请求
            </div>
          ) : null}
          {!approvalsLoading
            ? approvals.map((approval) => {
                const isSelf =
                  approval.requester.id === session.user.id;
                const canDecide =
                  session.capabilities.approve &&
                  approval.status === "pending" &&
                  !isSelf;
                const reason = rejectionReasons[approval.id] ?? "";
                return (
                  <article className="approval-row" key={approval.id}>
                    <div className="approval-qode">
                      <strong>{approval.qode.name}</strong>
                      <span>
                        /r/{approval.qode.slug} ·{" "}
                        {approval.actionType === "publish" ? "发布" : "回滚"}
                      </span>
                    </div>
                    <div className="approval-requester">
                      <strong>{approval.requester.displayName}</strong>
                      <span>
                        @{approval.requester.username} ·{" "}
                        {formatRelativeTime(approval.requestedAt)}
                      </span>
                    </div>
                    <div className="approval-payload">
                      {approval.payload.destination ? (
                        <span title={approval.payload.destination}>
                          {approval.payload.destination}
                        </span>
                      ) : approval.payload.releaseId ? (
                        <code>{approval.payload.releaseId}</code>
                      ) : (
                        <span>发布当前草稿</span>
                      )}
                      <small>
                        {approval.impact.deploymentQuantity.toLocaleString(
                          "zh-CN",
                        )}{" "}
                        个入口 · {approval.impact.deploymentBatches} 个批次
                      </small>
                      <small>{approval.payload.reason}</small>
                      <span
                        className={`approval-risk approval-risk-${approval.risk.level}`}
                      >
                        {approval.risk.level.toUpperCase()} · Policy v
                        {approval.policy.version}
                      </span>
                      {approval.policy.independentApprovalRequired ? (
                        <em>独立审批</em>
                      ) : null}
                    </div>
                    <div>
                      <span
                        className={`approval-status approval-status-${approval.status}`}
                      >
                        {approval.status === "approved" ? (
                          <CheckCircle2 size={13} />
                        ) : approval.status === "rejected" ? (
                          <CircleX size={13} />
                        ) : approval.status === "expired" ? (
                          <Clock3 size={13} />
                        ) : approval.status === "executing" ? (
                          <LoaderCircle className="spin" size={13} />
                        ) : (
                          <ClipboardCheck size={13} />
                        )}
                        {approvalStatusLabels[approval.status]}
                      </span>
                      {approval.status === "pending" ? (
                        <small>
                          {formatRelativeTime(approval.expiresAt)}到期
                        </small>
                      ) : null}
                      {approval.decisionReason &&
                      approval.status === "rejected" ? (
                        <small>{approval.decisionReason}</small>
                      ) : null}
                      {approval.executedRelease ? (
                        <small>
                          Release v{approval.executedRelease.version}
                        </small>
                      ) : null}
                    </div>
                    <div className="approval-actions">
                      {canDecide && rejectingId === approval.id ? (
                        <div className="approval-reject">
                          <input
                            value={reason}
                            maxLength={300}
                            autoFocus
                            placeholder="填写拒绝原因"
                            onChange={(event) =>
                              setRejectionReasons((current) => ({
                                ...current,
                                [approval.id]: event.target.value,
                              }))
                            }
                          />
                          <button
                            className="danger-button"
                            type="button"
                            disabled={
                              approvalWorking !== null ||
                              reason.trim().length < 2
                            }
                            onClick={() =>
                              void decideApproval(approval, "reject")
                            }
                          >
                            确认拒绝
                          </button>
                        </div>
                      ) : canDecide ? (
                        <>
                          <button
                            className="approval-approve"
                            type="button"
                            disabled={approvalWorking !== null}
                            onClick={() =>
                              void decideApproval(approval, "approve")
                            }
                          >
                            {approvalWorking === approval.id ? (
                              <LoaderCircle className="spin" size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                            通过
                          </button>
                          <button
                            className="approval-reject-open"
                            type="button"
                            disabled={approvalWorking !== null}
                            onClick={() => setRejectingId(approval.id)}
                          >
                            <CircleX size={14} />
                            拒绝
                          </button>
                        </>
                      ) : (
                        <span className="approval-decision-note">
                          {isSelf && approval.status === "pending"
                            ? "等待其他审批人"
                            : approval.decider
                              ? `${approval.decider.displayName} 已处理`
                              : "无需操作"}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })
            : null}
        </section>
      ) : null}
    </div>
  );
}
